import * as T from '^/types';
import { l10n } from '^/utilities/l10n';
import { z } from 'zod';

import Text from './text';

const emailSchema = z.string().email();
// const phoneNumberRegex = /^\+?[0-9]\d{9,15}$/;

export function isValidEmail(email: string) {
  return emailSchema.safeParse(email).success;
}

export enum SignUpSteps {
  STEP1 = 'STEP1',
  STEP2 = 'STEP2',
  STEP3 = 'STEP3',
  STEP4 = 'STEP4',
}

export const signUpStepsList = [
  SignUpSteps.STEP1,
  SignUpSteps.STEP2,
  SignUpSteps.STEP3,
  SignUpSteps.STEP4,
];

export const signUpInitialState = (email: string) => ({
  email: email ?? '',
  purpose: '',
  eula: false,
  password: '',
  lastName: '',
  firstName: '',
  organization: '',
  avatar: undefined,
  contactNumber: '',
  passwordConfirmation: '',
  language: T.Language.KO_KR,
  country: 'Korea, Republic of',
});

export interface SignUpFormFields {
  avatar: any;
  email: string;
  eula: boolean;
  purpose: string;
  country: string;
  password: string;
  lastName: string;
  firstName: string;
  organization: string;
  language: T.Language;
  contactNumber: string;
  passwordConfirmation: string;
}

export const passwordValidation = (language: T.Language) =>
  z
    .string()
    .nonempty(l10n(Text.error.password.required, language))
    .min(6, l10n(Text.error.password.minLength, language))
    .max(22, l10n(Text.error.password.maxLength, language))
    .regex(/^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{6,22}$/, {
      message: l10n(Text.error.password.pattern, language),
    })
    .regex(/^(?=.*[A-Z])(?=.*\d)(?=.*[!@$%^&*+\-_])[A-Za-z\d!@$%^&*+\-_]{6,22}$/, {
      message: l10n(Text.error.password.validCharacter, language),
    });

export function signupSchema(step: string, language: T.Language, isEmailExisted: boolean) {
  switch (step) {
    case SignUpSteps.STEP2:
      return z
        .object({
          email: z
            .string()
            .min(1, l10n(Text.error.email.required, language))
            .email(l10n(Text.error.email.pattern, language))
            .refine(
              async () => {
                if (isEmailExisted) {
                  return false;
                }
                return true;
              },
              {
                message: l10n(Text.error.email.existed, language),
              }
            ),
          password: passwordValidation(language),
          passwordConfirmation: z.string(),
        })
        .refine(data => data.password === data.passwordConfirmation, {
          path: ['passwordConfirmation'],
          message: l10n(Text.error.passwordConfirmation.equalPassword, language),
        });
    case SignUpSteps.STEP3:
      return z.object({
        firstName: z.string().trim().min(1, l10n(Text.error.firstName.required, language)),
        lastName: z.string().trim().min(1, l10n(Text.error.lastName.required, language)),
        organization: z.string().trim().min(1, l10n(Text.error.organization.required, language)),
        contactNumber: z.string().optional(),
        purpose: z.string().min(1, l10n(Text.error.purpose.required, language)),
      });
    case SignUpSteps.STEP4:
      return z.object({
        country: z.string().min(1, l10n(Text.error.country.required, language)),
        language: z.string().min(1, l10n(Text.error.language.required, language)),
        eula: z.boolean().refine(val => val, {
          message: l10n(Text.error.eula.required, language),
        }),
      });
    default:
      return z.object({});
  }
}
